import { addMonths, format } from "date-fns";
import {
  DayFlag,
  DayPicker,
  SelectionState,
  UI,
  useDayPicker,
} from "react-day-picker";
import type {
  ClassNames,
  DateRange,
  DayPickerProps,
  Labels,
  MonthCaptionProps,
} from "react-day-picker";
import type { Locale } from "date-fns";
import { cn } from "../../lib/cn";
import {
  AltArrowDownLinearIcon,
  AltArrowLeftLinearIcon,
  AltArrowRightLinearIcon,
} from "../../icons";

export type { DateRange };

export interface CalendarBaseProps {
  dir: "ltr" | "rtl";
  locale: Locale;
  nextMonthLabel?: string;
  previousMonthLabel?: string;
  labels?: Partial<Labels>;
}

export type CalendarProps = DayPickerProps &
  CalendarBaseProps &
  (
    | { nextMonthLabel: string; previousMonthLabel: string }
    | {
        labels: {
          labelNext: (date?: Date) => string;
          labelPrevious: (date?: Date) => string;
        } & Partial<Labels>;
      }
  );

const currentYear = new Date().getFullYear();
// Wide enough for birthdates and multi-year subscription terms; callers can
// still narrow it via startMonth/endMonth.
const defaultStartMonth = new Date(currentYear - 100, 0, 1);
const defaultEndMonth = new Date(currentYear + 10, 11, 1);

const NAV_BUTTON =
  "inline-flex size-6 items-center justify-center rounded-full text-text-placeholder transition-[background-color,scale] duration-140 ease-emphasized hover:bg-container-hover active:scale-[0.94] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-40";

/**
 * Figma "Month /Year" (Date & Time pickers 2818:116890): the month between
 * its previous/next arrows on the start side, the year as a dropdown on the
 * end side. With two months side by side, only the outer arrows show.
 */
function CalendarCaption({
  calendarMonth,
  displayIndex,
  className,
  ...rest
}: MonthCaptionProps) {
  const {
    goToMonth,
    previousMonth,
    nextMonth,
    months,
    labels,
    dayPickerProps,
  } = useDayPicker();
  const month = calendarMonth.date;
  // The Calendar always passes a full date-fns locale.
  const locale = dayPickerProps.locale as Locale | undefined;
  const start = dayPickerProps.startMonth ?? defaultStartMonth;
  const end = dayPickerProps.endMonth ?? defaultEndMonth;
  const years = Array.from(
    { length: end.getFullYear() - start.getFullYear() + 1 },
    (_, index) => start.getFullYear() + index,
  );
  const isFirst = displayIndex === 0;
  const isLast = displayIndex === months.length - 1;

  return (
    <div
      {...rest}
      className={cn("flex items-center justify-between", className)}
    >
      <div className="flex items-center gap-1 px-1 py-2">
        {isFirst ? (
          <button
            type="button"
            className={NAV_BUTTON}
            disabled={previousMonth === undefined}
            aria-label={labels.labelPrevious(previousMonth)}
            onClick={() => {
              if (previousMonth !== undefined) goToMonth(previousMonth);
            }}
          >
            {/* Points back in either reading direction. */}
            <AltArrowLeftLinearIcon
              className="size-4 rtl:rotate-180"
              aria-hidden="true"
            />
          </button>
        ) : null}
        <span
          className="px-1 text-body-m-b text-text-placeholder"
          aria-live="polite"
        >
          {format(month, "LLLL", locale === undefined ? {} : { locale })}
        </span>
        {isLast ? (
          <button
            type="button"
            className={NAV_BUTTON}
            disabled={nextMonth === undefined}
            aria-label={labels.labelNext(nextMonth)}
            onClick={() => {
              if (nextMonth !== undefined) goToMonth(nextMonth);
            }}
          >
            <AltArrowRightLinearIcon
              className="size-4 rtl:rotate-180"
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>
      <label className="relative flex cursor-pointer items-center gap-1 rounded-full p-1 hover:bg-container-hover focus-within:ring-1 focus-within:ring-primary">
        <AltArrowDownLinearIcon
          className="size-4 text-text-placeholder"
          aria-hidden="true"
        />
        <span className="px-1 text-body-m-b text-text-placeholder">
          {month.getFullYear()}
        </span>
        {/* A native select over the label: the full keyboard and screen
            reader behavior of a list box, the look of the design. */}
        <select
          aria-label={labels.labelYearDropdown()}
          value={month.getFullYear()}
          onChange={(event) => {
            const picked = new Date(
              Number(event.target.value),
              month.getMonth(),
              1,
            );
            // Keep this month in the same slot when several are shown.
            goToMonth(addMonths(picked, -displayIndex));
          }}
          className="absolute inset-0 cursor-pointer opacity-0"
        >
          {years.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

/**
 * The shared calendar (Figma Date & Time pickers 2818:116890): a white card
 * with the month/year row, the weekday names on a grey strip, and 40px round
 * days -- the picked day (or a range's ends) filled in the primary color.
 * Weeks start on Sunday, as in the design.
 */
export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  dir,
  locale,
  nextMonthLabel,
  previousMonthLabel,
  labels,
  components,
  startMonth = defaultStartMonth,
  endMonth = defaultEndMonth,
  weekStartsOn = 0,
  ...props
}: CalendarProps) {
  const resolvedLabels: Partial<Labels> = {
    labelPrevious: () => previousMonthLabel ?? "",
    labelNext: () => nextMonthLabel ?? "",
    ...labels,
  };

  const defaultClassNames: Partial<ClassNames> = {
    [UI.Root]: "rounded-lg bg-container-default-background p-2",
    [UI.Months]: "relative flex flex-col gap-4 sm:flex-row",
    [UI.Month]: "flex flex-col gap-2",
    [UI.MonthCaption]: "",
    [UI.MonthGrid]: "w-full border-collapse",
    [UI.Weekdays]: "flex overflow-hidden rounded-lg bg-neutral-50",
    [UI.Weekday]:
      "flex h-6 flex-1 items-center justify-center p-0 text-caption-s font-normal text-text-placeholder select-none",
    [UI.Weeks]: "mt-2 flex flex-col",
    [UI.Week]: "flex",
    [UI.Day]:
      "relative flex h-10 min-w-10 flex-1 items-center justify-center p-0 text-center focus-within:relative focus-within:z-20",
    [UI.DayButton]:
      "flex size-10 items-center justify-center rounded-full p-0 text-body-s-r text-calendar-day-text transition-[background-color,color,scale] duration-140 ease-emphasized select-none hover:bg-container-hover active:scale-[0.94] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary",
    [SelectionState.selected]:
      "[&>button]:bg-primary [&>button]:text-neutral-white [&>button]:hover:bg-primary",
    // A range's ends are filled circles; the band between them is a light
    // tint that runs under the ends' inner halves.
    [SelectionState.range_start]:
      "bg-blue-purple-50 rounded-s-full [&>button]:bg-primary [&>button]:text-neutral-white",
    [SelectionState.range_middle]:
      "bg-blue-purple-50 [&>button]:bg-transparent [&>button]:text-calendar-day-text [&>button]:hover:bg-blue-purple-100",
    [SelectionState.range_end]:
      "bg-blue-purple-50 rounded-e-full [&>button]:bg-primary [&>button]:text-neutral-white",
    [DayFlag.today]:
      "[&:not([data-selected])>button]:border [&:not([data-selected])>button]:border-primary",
    [DayFlag.outside]: "",
    [DayFlag.disabled]:
      "cursor-not-allowed [&>button]:pointer-events-none [&>button]:text-transparent-black-30",
    [DayFlag.hidden]: "invisible",
  };

  const mergedClassNames: Partial<ClassNames> = {
    ...defaultClassNames,
    ...classNames,
  };
  for (const key of Object.keys(defaultClassNames) as Array<keyof ClassNames>) {
    if (classNames?.[key]) {
      mergedClassNames[key] = cn(defaultClassNames[key], classNames[key]);
    }
  }

  return (
    <DayPicker
      dir={dir}
      locale={locale}
      showOutsideDays={showOutsideDays}
      weekStartsOn={weekStartsOn}
      // The caption below carries its own month arrows and year dropdown.
      hideNavigation
      startMonth={startMonth}
      endMonth={endMonth}
      className={cn(className)}
      classNames={mergedClassNames}
      components={{ MonthCaption: CalendarCaption, ...components }}
      labels={resolvedLabels}
      {...props}
    />
  );
}
